import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import { apiFetch } from '../services/api';
import { useSocketStore } from './socket';
import { useEntitiesStore } from './entities';
import type { Device } from '@/types';
import { isAccessDevice } from '../utils/accessDevices';

// ─── Types ────────────────────────────────────────────────────────────────────

export type SecurityDeviceType = 'SENSOR' | 'OUTPUT' | 'PANEL' | 'LOCK' | 'CAMERA' | 'CLOSURE';

export type SecuritySensorModeState = 'disarmed' | 'supervise' | 'armed';

/** Fixed id of the system Disarmed preset. */
export const DISARMED_MODE_ID = 'disarmed';
export type SecurityAlarmEventType =
  | 'ARMED'
  | 'DISARMED'
  | 'ALARM_TRIGGERED'
  | 'ALARM_CLEARED'
  | 'SENSOR_BYPASS'
  | 'SENSOR_TAMPER'
  | 'PANIC'
  | 'DURESS_DISARM'
  | 'AUTO_ARM'
  | 'AUTO_DISARM'
  | 'MODE_CHANGED';
export type SecurityContactType = 'SMS' | 'CALL' | 'EMAIL' | 'WEBHOOK' | 'PUSH';

export interface SecurityModePreset {
  id: string;
  name: string;
  icon: string;
  sortOrder: number;
  config: Record<
    string,
    {
      state?: SecuritySensorModeState | 'locked' | 'unlocked';
      delay?: number;
      /** Legacy arming flag. */
      armed?: boolean;
      /** Camera entry: mute motion notifications while the mode is active. */
      muteMotionNotifications?: boolean;
    }
  >;
  createdAt: string;
  updatedAt: string;
}

export interface SecurityDeviceEntry {
  id: string;
  deviceId: string;
  deviceType: SecurityDeviceType;
  bypass: boolean;
  enabled: boolean;
  sortOrder: number;
  config: Record<string, unknown> | null;
  device: {
    id: string;
    deviceName: string;
    deviceControl: string | null;
    deviceType?: string | null;
    icon?: string | null;
    settings?: SecurityDeviceSettings | null;
    roomId?: string | null;
    room: { id: string; roomName: string } | null;
  };
}

/**
 * A security device's own settings bag — whatever the enrolment's picker chose
 * (`detectedAs` and the sensor's own fields).
 */
export interface SecurityDeviceSettings {
  [key: string]: unknown;
}

export interface SecurityAlarmLogEntry {
  id: string;
  eventType: SecurityAlarmEventType;
  deviceId: string | null;
  modeId: string | null;
  triggeredByUserId: string | null;
  details: Record<string, unknown> | null;
  createdAt: string;
  device: { id: string; device: { id: string; deviceName: string } | null } | null;
  mode: { id: string; name: string } | null;
  triggeredByUser: { id: string; username: string; fullName: string | null } | null;
}

export interface SecurityEmergencyContact {
  id: string;
  contactType: SecurityContactType;
  contactName: string;
  phoneNumber: string | null;
  apiEndpoint: string | null;
  autoCall: boolean;
  callDelaySeconds: number;
  enabled: boolean;
  sortOrder: number;
}

export interface SecurityRuntimeState {
  activeModeId: string | null;
  alarmActive: boolean;
  alarmTriggeredAt: string | null;
}

/**
 * The user-editable half of `/security/config` (presence / auto-arm). The row
 * carries more columns than these; the pages only ever read or write these.
 */
export interface SecurityConfigValues {
  autoArmOnLeave?: boolean;
  autoDisarmOnArrive?: boolean;
  autoArmModeId?: string | null;
}

// ─── State Event from Socket.IO ───────────────────────────────────────────────

interface SecurityStateEvent {
  activeModeId: string | null;
  alarmActive: boolean;
  alarmTriggeredAt: string | null;
  /** Seconds remaining on the longest pending arming delay (or exit delay). */
  armingCountdown?: number | null;
  status: string | null;
}

// ─── Security Device Type Display Config ──────────────────────────────────────

export const SECURITY_TYPE_ICONS: Record<string, string> = {
  camera: 'videocam',
  lock: 'lock',
  sensor: 'sensors',
  output: 'volume_up',
  panel: 'devices',
  closure: 'garage',
};

/**
 * Each type's group heading, in the app's own pair shape (`label: ['EN', 'SL']`)
 * and rendered by the view through `tt(...pair)`. The keys are the API's enum
 * values, compared in code and never painted.
 */
export const SECURITY_TYPE_LABELS: Record<string, [string, string]> = {
  camera: ['Cameras', 'Kamere'],
  lock: ['Locks', 'Ključavnice'],
  sensor: ['Sensors', 'Senzorji'],
  output: ['Outputs', 'Izhodi'],
  panel: ['Panels', 'Plošče'],
  closure: ['Closures', 'Vrata in ograje'],
};

export const SECURITY_TYPE_ORDER = ['camera', 'lock', 'closure', 'sensor', 'output', 'panel'];

export function getSecurityTypeIcon(type: string): string {
  return SECURITY_TYPE_ICONS[type.toLowerCase()] ?? 'shield';
}

/** The type's heading in both languages; an unknown type reads as its own key. */
export function getSecurityTypeLabel(type: string): [string, string] {
  return SECURITY_TYPE_LABELS[type.toLowerCase()] ?? [type, type];
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useSecurityStore = defineStore('security', () => {
  const entitiesStore = useEntitiesStore();
  const socketStore = useSocketStore();

  // State
  const modes = ref<SecurityModePreset[]>([]);
  const securityDevices = ref<SecurityDeviceEntry[]>([]);
  const activeModeId = ref<string | null>(null);
  const alarmActive = ref(false);
  const alarmTriggeredAt = ref<string | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const alarmLog = ref<SecurityAlarmLogEntry[]>([]);
  /** The `/security/config` row, so a page that already loaded it need not ask twice. */
  const config = ref<SecurityConfigValues | null>(null);

  /** Seconds remaining on the longest pending arming delay (null when idle). */
  const armingCountdown = ref<number | null>(null);
  let armingTicker: ReturnType<typeof setInterval> | null = null;

  function _setArmingCountdown(value: number | null) {
    armingCountdown.value = value;
    if (armingTicker) {
      clearInterval(armingTicker);
      armingTicker = null;
    }
    if (value !== null && value > 0) {
      armingTicker = setInterval(() => {
        if (armingCountdown.value !== null) {
          armingCountdown.value -= 1;
          if (armingCountdown.value <= 0) {
            armingCountdown.value = null;
            if (armingTicker) {
              clearInterval(armingTicker);
              armingTicker = null;
            }
          }
        }
      }, 1000);
    }
  }

  // ── Computed ────────────────────────────────────────────────────────────────

  /** The currently active mode preset (if any) */
  const activeMode = computed(() => modes.value.find((m) => m.id === activeModeId.value) ?? null);

  /** All devices that belong to security, grouped by their device type */
  const devicesByType = computed<Map<string, Device[]>>(() => {
    const mapped = new Map<string, Device[]>();

    for (const sd of securityDevices.value) {
      if (!sd.enabled) continue;
      const device = entitiesStore.getDevice(sd.deviceId);
      if (!device) continue;

      // Determine display group
      let group: string;
      if (sd.deviceType === 'SENSOR') group = 'sensor';
      else if (sd.deviceType === 'OUTPUT') group = 'output';
      else if (sd.deviceType === 'LOCK') group = 'lock';
      else if (sd.deviceType === 'CLOSURE') group = 'closure';
      else if (sd.deviceType === 'PANEL') group = 'panel';
      else group = 'other';

      // Camera override: check config.detectedAs first (set by picker), then deviceControl
      const detectedAs = sd.config?.detectedAs as string | undefined;
      if (detectedAs === 'CAMERA') {
        group = 'camera';
      } else {
        const dc = device.deviceControl?.toLowerCase() ?? '';
        if (dc === 'camera') group = 'camera';
      }

      if (!mapped.has(group)) mapped.set(group, []);
      mapped.get(group)!.push(device);
    }

    // Sort each group by sortOrder
    for (const [, devices] of mapped) {
      devices.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    }

    // Return sorted by canonical order
    return new Map(
      [...mapped.entries()].sort(([a], [b]) => {
        const ai = SECURITY_TYPE_ORDER.indexOf(a);
        const bi = SECURITY_TYPE_ORDER.indexOf(b);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      }),
    );
  });

  // ── Socket ──────────────────────────────────────────────────────────────────

  function _handleStateUpdated(event: SecurityStateEvent) {
    activeModeId.value = event.activeModeId;
    alarmActive.value = event.alarmActive;
    alarmTriggeredAt.value = event.alarmTriggeredAt;
    if (typeof event.armingCountdown === 'number') {
      _setArmingCountdown(event.armingCountdown > 0 ? event.armingCountdown : null);
    }
  }

  function _handleAlarmTriggered() {
    alarmActive.value = true;
  }

  function _registerSocketListeners() {
    const s = socketStore.socket;
    if (!s) return;
    s.off('security:state_updated', _handleStateUpdated);
    s.on('security:state_updated', _handleStateUpdated);
    s.off('security:alarm_triggered', _handleAlarmTriggered);
    s.on('security:alarm_triggered', _handleAlarmTriggered);
  }

  /**
   * One binding per socket handle, not one per page visit: this used to install
   * a fresh `watch` on every mount (`Security.vue`, `SettingsSecurity.vue`), so
   * each visit left another store-level effect behind.
   */
  let socketWatcherInstalled = false;

  function initSocketListeners() {
    _registerSocketListeners();
    if (socketWatcherInstalled) return;
    socketWatcherInstalled = true;
    watch(
      () => socketStore.socket,
      (s) => {
        if (s) _registerSocketListeners();
      },
    );
  }

  // ── API: Modes ──────────────────────────────────────────────────────────────

  async function fetchModes() {
    try {
      modes.value = await apiFetch('/security/modes');
    } catch (err) {
      console.error('[SecurityStore] fetchModes error', err);
    }
  }

  async function createMode(data: {
    name: string;
    icon?: string;
    sortOrder?: number;
    config?: Record<string, { state?: string; delay?: number; muteMotionNotifications?: boolean }>;
  }): Promise<SecurityModePreset> {
    const mode = await apiFetch('/security/modes', { method: 'POST', body: JSON.stringify(data) });
    modes.value.push(mode);
    return mode;
  }

  async function updateMode(
    modeId: string,
    data: Partial<Omit<SecurityModePreset, 'config'>> & {
      config?: Record<string, { state?: string; delay?: number; muteMotionNotifications?: boolean }>;
    },
  ): Promise<SecurityModePreset> {
    const updated = await apiFetch(`/security/modes/${modeId}`, { method: 'PUT', body: JSON.stringify(data) });
    const idx = modes.value.findIndex((m) => m.id === modeId);
    if (idx !== -1) modes.value[idx] = { ...modes.value[idx], ...updated };
    return updated;
  }

  async function deleteMode(modeId: string): Promise<void> {
    await apiFetch(`/security/modes/${modeId}`, { method: 'DELETE' });
    modes.value = modes.value.filter((m) => m.id !== modeId);
    if (activeModeId.value === modeId) activeModeId.value = null;
  }

  async function reorderModes(modeIds: string[]): Promise<void> {
    const updated = await apiFetch('/security/modes/reorder', { method: 'PATCH', body: JSON.stringify({ modeIds }) });
    modes.value = updated;
  }

  /**
   * Load the built-in default arm modes (Presence / Sleep / Absence).
   * Idempotent — modes that already exist (by fixed id) are left untouched.
   */
  async function loadDefaultModes(): Promise<{ created: string[] }> {
    const result = await apiFetch('/security/modes/defaults', { method: 'POST' });
    if (Array.isArray(result?.modes)) {
      modes.value = result.modes;
    } else {
      await fetchModes();
    }
    return { created: Array.isArray(result?.created) ? result.created : [] };
  }

  async function activateMode(modeId: string, pin?: string): Promise<{ success: boolean; message: string }> {
    // Activating the Disarmed preset is a disarm.
    if (modeId === DISARMED_MODE_ID) {
      return disarm(pin);
    }
    const result = await apiFetch(`/security/modes/${modeId}/activate`, {
      method: 'POST',
      body: JSON.stringify({ pin }),
    });
    if (!result.success) {
      throw new Error(result.message || 'Failed to activate security mode');
    }
    activeModeId.value = modeId;
    return result;
  }

  async function disarm(pin?: string): Promise<{ success: boolean; message: string }> {
    const result = await apiFetch('/security/disarm', { method: 'POST', body: JSON.stringify({ pin }) });
    if (!result.success) {
      throw new Error(result.message || 'Failed to disarm security');
    }
    activeModeId.value = null;
    alarmActive.value = false;
    return result;
  }

  async function panicAlarm(): Promise<void> {
    await apiFetch('/security/panic', { method: 'POST' });
  }

  // ── API: State / Config ────────────────────────────────────────────────────

  async function fetchConfig() {
    try {
      const data = await apiFetch('/security/config');
      if (data.runtime) {
        activeModeId.value = data.runtime.activeModeId;
        alarmActive.value = data.runtime.alarmActive;
        alarmTriggeredAt.value = data.runtime.alarmTriggeredAt;
      }
      config.value = data.config ?? null;
      return data.config;
    } catch (err) {
      console.error('[SecurityStore] fetchConfig error', err);
      return null;
    }
  }

  // ── API: Security Devices ──────────────────────────────────────────────────

  async function fetchSecurityDevices() {
    try {
      securityDevices.value = await apiFetch('/security/devices');
    } catch (err) {
      console.error('[SecurityStore] fetchSecurityDevices error', err);
    }
  }

  async function addSecurityDevice(data: {
    deviceId: string;
    deviceType: string;
    bypass?: boolean;
    enabled?: boolean;
    sortOrder?: number;
    config?: Record<string, unknown>;
  }): Promise<SecurityDeviceEntry> {
    const device = await apiFetch('/security/devices', { method: 'POST', body: JSON.stringify(data) });
    // Re-fetch full list so we get device relation (deviceControl, room, etc.)
    await fetchSecurityDevices();
    return device;
  }

  async function updateSecurityDevice(
    deviceId: string,
    data: { bypass?: boolean; enabled?: boolean; sortOrder?: number; config?: Record<string, unknown> },
  ): Promise<SecurityDeviceEntry> {
    const updated = await apiFetch(`/security/devices/${deviceId}`, { method: 'PATCH', body: JSON.stringify(data) });
    // Re-fetch full list to get fresh relations
    await fetchSecurityDevices();
    return updated;
  }

  async function removeSecurityDevice(deviceId: string): Promise<void> {
    await apiFetch(`/security/devices/${deviceId}`, { method: 'DELETE' });
    securityDevices.value = securityDevices.value.filter((d) => d.id !== deviceId);
  }

  async function reorderSecurityDevices(deviceIds: string[]): Promise<void> {
    const updated = await apiFetch('/security/devices/reorder', {
      method: 'PATCH',
      body: JSON.stringify({ deviceIds }),
    });
    securityDevices.value = updated;
  }

  /** A device the sync dropped, as the hub reports it. */
  interface RemovedDeviceRef {
    deviceId?: string;
    deviceName?: string;
    [key: string]: unknown;
  }

  /** Reconcile enrollments with the backend's internal sync list (devices
   *  with a mode-settable security value). Returns the sync report. */
  async function syncSecurityDevices(): Promise<{
    added: SecurityDeviceEntry[];
    removed: RemovedDeviceRef[];
    devices: SecurityDeviceEntry[];
  }> {
    const result = await apiFetch('/security/devices/sync', { method: 'POST', body: JSON.stringify({}) });
    if (Array.isArray(result.devices)) securityDevices.value = result.devices;
    return result;
  }

  // ── Compatible device scanning ──────────────────────────────────────────────

  /** deviceControl values that are candidates for security enrolment. */
  const COMPATIBLE_DEVICE_CONTROLS = [
    'sensor',
    'binary_sensor',
    'lock',
    'strike_lock',
    'motorized_lock',
    'alarm',
    'camera',
  ];

  /** Every device that could be enrolled in security but isn't yet. */
  const compatibleDevices = computed(() => {
    const allDevices = entitiesStore.devices;
    if (!allDevices || allDevices.length === 0) return [];
    const existingIds = new Set(securityDevices.value.map((sd) => sd.deviceId));
    return allDevices.filter((d) => {
      if (existingIds.has(d.id)) return false;
      const dc = (d.deviceControl ?? '').toLowerCase();
      if (COMPATIBLE_DEVICE_CONTROLS.some((c) => dc === c)) return true;
      if (dc.endsWith('_lock')) return true;
      if (dc.includes('camera') || dc === 'video') return true;
      if (dc.includes('_sensor')) return true;
      if (dc === 'switch' || dc === 'output') return true;
      // Closures — garage doors and gates, which the picker enrolls as CLOSURE.
      if (isAccessDevice(d)) return true;
      return (d.deviceValues ?? []).some((v) => {
        const key = (v.valueKey ?? '').toLowerCase();
        return key.includes('alarm') || key.includes('motion') || key.includes('contact') || key.includes('tamper');
      });
    });
  });

  /** Devices that can be enrolled as sirens/outputs (separate Sirens tab). */
  const sirenCompatibleDevices = computed(() => {
    const allDevices = entitiesStore.devices;
    if (!allDevices || allDevices.length === 0) return [];
    const existingIds = new Set(securityDevices.value.map((sd) => sd.deviceId));
    return allDevices.filter((d) => {
      if (existingIds.has(d.id)) return false;
      const dc = (d.deviceControl ?? '').toLowerCase();
      return dc === 'alarm' || dc === 'output' || dc.includes('siren') || dc === 'switch';
    });
  });

  /** Otto touch panels that can be enrolled as alarm control panels. */
  const panelCompatibleDevices = computed(() => {
    const allDevices = entitiesStore.devices;
    if (!allDevices || allDevices.length === 0) return [];
    const existingIds = new Set(securityDevices.value.map((sd) => sd.deviceId));
    return allDevices.filter((d) => {
      if (existingIds.has(d.id)) return false;
      const dc = (d.deviceControl ?? '').toLowerCase();
      const dt = (d.deviceType ?? '').toLowerCase();
      return dc === 'touch_panel' || dt === 'otto_touch_panel';
    });
  });

  // ── API: Config ────────────────────────────────────────────────────────────

  async function saveConfig(data: SecurityConfigValues): Promise<void> {
    const result = await apiFetch('/security/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (result?.config) config.value = result.config;
  }

  // ── API: Alarm Log ─────────────────────────────────────────────────────────

  async function fetchAlarmLog(limit = 50): Promise<SecurityAlarmLogEntry[]> {
    try {
      const data = await apiFetch(`/security/log?limit=${limit}`);
      alarmLog.value = data;
      return data;
    } catch (err) {
      console.error('[SecurityStore] fetchAlarmLog error', err);
      return [];
    }
  }

  // ── API: Emergency Contacts ────────────────────────────────────────────────

  async function saveContact(data: Partial<SecurityEmergencyContact>): Promise<SecurityEmergencyContact> {
    const method = data.id ? 'PUT' : 'POST';
    const url = data.id ? `/security/contacts/${data.id}` : '/security/contacts';
    return apiFetch(url, { method, body: JSON.stringify(data) });
  }

  async function deleteContact(contactId: string): Promise<void> {
    await apiFetch(`/security/contacts/${contactId}`, { method: 'DELETE' });
  }

  // ── API: User PINs ─────────────────────────────────────────────────────────

  async function setUserSecurityPin(userId: string, pin: string | null): Promise<void> {
    await apiFetch(`/security/users/${userId}/security-pin`, { method: 'PUT', body: JSON.stringify({ pin }) });
  }

  async function setUserDuressPin(userId: string, pin: string | null): Promise<void> {
    await apiFetch(`/security/users/${userId}/duress-pin`, { method: 'PUT', body: JSON.stringify({ pin }) });
  }

  // ── Load All ────────────────────────────────────────────────────────────────

  async function loadAll() {
    loading.value = true;
    error.value = null;
    try {
      await Promise.all([fetchModes(), fetchSecurityDevices(), fetchConfig()]);
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err.message ?? 'Failed to load security data';
    } finally {
      loading.value = false;
    }
  }

  return {
    // State
    modes,
    securityDevices,
    activeModeId,
    alarmActive,
    alarmTriggeredAt,
    loading,
    error,
    alarmLog,
    config,
    armingCountdown,
    // Computed
    activeMode,
    devicesByType,
    // Socket
    initSocketListeners,
    // Load
    loadAll,
    // Modes
    fetchModes,
    createMode,
    updateMode,
    deleteMode,
    reorderModes,
    loadDefaultModes,
    activateMode,
    disarm,
    panicAlarm,
    // Config
    fetchConfig,
    saveConfig,
    // Devices
    fetchSecurityDevices,
    addSecurityDevice,
    updateSecurityDevice,
    removeSecurityDevice,
    reorderSecurityDevices,
    syncSecurityDevices,
    compatibleDevices,
    sirenCompatibleDevices,
    panelCompatibleDevices,
    // Log
    fetchAlarmLog,
    // Contacts
    saveContact,
    deleteContact,
    // PINs
    setUserSecurityPin,
    setUserDuressPin,
  };
});
